// src/domains/catalog/pages/client/CatalogPage.jsx
// Hub de estabelecimentos: exibe todos imediatamente, categorias funcionam como filtros
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { establishmentsService, usersService } from 'infrastructure/firebase';
import { Avatar } from 'shared/components/ui';
import { useMobile } from 'shared/hooks/useMobile';
import { ESTABLISHMENT_CATEGORIES } from 'shared/config/plans';

const T = {
  brand: '#6B5876', accent: '#C9929A', border: '#E8E5EC',
  s2: '#FAF7FB', s3: '#F0EDF3', text: '#3D3447', text2: '#6B6070', muted: '#A8A1B0',
  success: '#8FC2A6', successBg: '#E7F4ED', successFg: '#2D5F43',
  sans: "'DM Sans',sans-serif", serif: "'Instrument Serif',serif",
};

// Categorias de filtro — 'all' mostra tudo, 'autonomo' filtra só profissionais sem vínculo
const FILTER_CATEGORIES = [
  { id: 'all',         label: 'Todos',        icon: '🔍' },
  { id: 'salao',       label: 'Salão',         icon: '💇' },
  { id: 'estetica',    label: 'Estética',      icon: '✨' },
  { id: 'odontologia', label: 'Odontologia',   icon: '🦷' },
  { id: 'medica',      label: 'Medicina',      icon: '🏥' },
  { id: 'autonomo',    label: 'Autônomos',     icon: '👤' },
];

// Tags de especialidade por categoria (para filtrar profissionais autônomos)
const CATEGORY_TAGS = {
  salao:       ['cabelo', 'beleza', 'manicure', 'pedicure', 'estética', 'salão', 'barbeiro', 'barbearia'],
  estetica:    ['estética', 'massagem', 'skincare', 'depilação', 'micropigmentação', 'limpeza de pele'],
  odontologia: ['dentista', 'odontologia', 'ortodontia', 'implante', 'odonto'],
  medica:      ['médico', 'medicina', 'consulta', 'dermatologia', 'pediatria', 'ginecologia'],
};

// ── Card de estabelecimento ─────────────────────────────────────────────────────
// height: 100% garante mesma altura em grids com align-items: stretch (padrão do CSS Grid)
function ClinicCard({ clinic, onSelect }) {
  const catInfo = ESTABLISHMENT_CATEGORIES.find(c => c.id === clinic.categoria);
  return (
    <div onClick={onSelect}
      style={{
        background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16,
        cursor: 'pointer', transition: 'box-shadow 0.15s',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        display: 'flex', flexDirection: 'column', // flex column para altura igual no grid
        overflow: 'visible', // não clipar foto com margem negativa
      }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(107,88,118,0.12)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'}>

      {/* Banner — proporção fixa via paddingTop */}
      <div style={{
        height: 80, flexShrink: 0, borderRadius: '16px 16px 0 0', overflow: 'hidden',
        position: 'relative',
        background: clinic.banner
          ? `url(${clinic.banner}) center/cover`
          : `linear-gradient(135deg,#6B5876,#B5A2D2)`,
      }}>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.12)' }} />
      </div>

      {/* Conteúdo — flex:1 para ocupar espaço disponível */}
      <div style={{ padding: '0 14px 16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Foto — z-index para ficar acima do banner sem clipar */}
        <div style={{
          width: 52, height: 52, flexShrink: 0, borderRadius: 12,
          border: '3px solid #fff', boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
          marginTop: -26, marginBottom: 10,
          background: clinic.foto ? `url(${clinic.foto}) center/cover no-repeat` : T.s3,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22, overflow: 'hidden',
          position: 'relative', zIndex: 1, // acima do banner
        }}>
          {!clinic.foto && (catInfo?.icon || '🏥')}
        </div>

        {/* Nome com proteção contra overflow */}
        <div style={{
          fontWeight: 600, fontSize: 14, color: T.text, marginBottom: 2,
          wordBreak: 'break-word', overflowWrap: 'break-word',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {clinic.nomeFantasia || clinic.razaoSocial}
        </div>
        <div style={{ fontSize: 11, color: T.muted, marginBottom: 6, flexShrink: 0 }}>
          {catInfo?.label || 'Estabelecimento'}
        </div>
        {/* Descrição com clamp — flex:1 para empurrar categoria para cima */}
        {clinic.descricao && (
          <div style={{
            fontSize: 12, color: T.text2, lineHeight: 1.5,
            overflow: 'hidden', display: '-webkit-box',
            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            flex: 1,
          }}>
            {clinic.descricao}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Card de profissional autônomo ───────────────────────────────────────────────
function ProfessionalCard({ pro, onSelect }) {
  return (
    <div onClick={onSelect}
      style={{
        background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16,
        padding: 16, cursor: 'pointer',
        display: 'flex', gap: 14, alignItems: 'flex-start',
        transition: 'box-shadow 0.15s', boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
      }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(107,88,118,0.12)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'}>

      {/* Avatar/foto com tamanho fixo para não deformar o card */}
      <div style={{ flexShrink: 0, width: 56 }}>
        {pro.photo
          ? <div style={{ width: 56, height: 56, borderRadius: '50%',
              backgroundImage: `url(${pro.photo})`, backgroundSize: 'cover',
              backgroundPosition: 'center', border: `2px solid ${T.border}` }} />
          : <Avatar name={pro.name} color={pro.avatarColor || 'lavender'} size="lg" />}
      </div>

      {/* Conteúdo com minWidth:0 para permitir truncamento de texto */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontWeight: 600, fontSize: 14, color: T.text,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {pro.name}
        </div>
        <div style={{
          fontSize: 12, color: T.muted, marginTop: 2,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {pro.specialty}
        </div>
        {pro.descricaoProfissional && (
          <div style={{
            fontSize: 12, color: T.text2, marginTop: 6, lineHeight: 1.5,
            overflow: 'hidden', display: '-webkit-box',
            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>
            {pro.descricaoProfissional}
          </div>
        )}
        <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {pro.atendimentoDomiciliar && (
            <span style={{ fontSize: 11, padding: '2px 10px', borderRadius: 20,
              background: T.successBg, color: T.successFg, fontWeight: 500 }}>
              🏠 Domiciliar
            </span>
          )}
          {pro.endereco && !pro.atendimentoDomiciliar && (
            <span style={{
              fontSize: 11, color: T.muted,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              maxWidth: 160,
            }}>
              📍 {pro.endereco.split(',')[0]}
            </span>
          )}
          <span style={{ fontSize: 11, padding: '2px 10px', borderRadius: 20,
            background: T.s3, color: T.brand, fontWeight: 500, whiteSpace: 'nowrap' }}>
            Autônomo
          </span>
        </div>
      </div>

      <div style={{ flexShrink: 0, fontSize: 18, color: T.muted, alignSelf: 'center' }}>›</div>
    </div>
  );
}

// ── Skeleton de carregamento ────────────────────────────────────────────────────
function CardSkeleton() {
  return (
    <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden' }}>
      <div style={{ height: 80, background: 'linear-gradient(90deg,#F0EDF3 25%,#E8E5EC 50%,#F0EDF3 75%)',
        backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
      <div style={{ padding: '10px 14px 16px' }}>
        <div style={{ width: 52, height: 52, borderRadius: 12, marginTop: -20, marginBottom: 10,
          background: 'linear-gradient(90deg,#F0EDF3 25%,#E8E5EC 50%,#F0EDF3 75%)',
          backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
        <div style={{ height: 14, width: '70%', borderRadius: 8, marginBottom: 8,
          background: 'linear-gradient(90deg,#F0EDF3 25%,#E8E5EC 50%,#F0EDF3 75%)',
          backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
        <div style={{ height: 11, width: '45%', borderRadius: 8,
          background: 'linear-gradient(90deg,#F0EDF3 25%,#E8E5EC 50%,#F0EDF3 75%)',
          backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
      </div>
    </div>
  );
}

// ── Página principal ────────────────────────────────────────────────────────────
export default function CatalogPage() {
  const { category }  = useParams(); // define o filtro inicial via URL
  const navigate      = useNavigate();
  const isMobile      = useMobile();

  const [allClinics, setAllClinics]           = useState([]);
  const [allProfessionals, setAllProfessionals] = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [search, setSearch]                   = useState('');
  const [cityFilter, setCityFilter]           = useState('');
  const [activeFilter, setActiveFilter]       = useState(category || 'all');

  // Sincroniza filtro com param de URL (ex: /client/catalog/salao)
  useEffect(() => {
    if (category && FILTER_CATEGORIES.some(f => f.id === category)) {
      setActiveFilter(category);
    }
  }, [category]);

  // Carrega TODOS os estabelecimentos e profissionais ao montar
  useEffect(() => {
    setLoading(true);
    Promise.all([
      establishmentsService.getAll(),
      usersService.getByRole('professional'),
    ]).then(([estabs, pros]) => {
      setAllClinics(estabs.filter(e => e.ativo !== false));
      // Profissionais autônomos: sem vínculo com estabelecimento
      setAllProfessionals(pros.filter(p => !p.estabelecimentoId && p.active !== false));
    }).catch(err => console.error('[Catalog] Erro ao carregar dados:', err))
      .finally(() => setLoading(false));
  }, []);

  // Filtro de categoria aplicado client-side (sem nova requisição ao Firestore)
  const filteredClinics = useMemo(() => {
    return allClinics.filter(c => {
      // Sem categoria: não mostra estabelecimentos
      if (activeFilter === 'autonomo') return false;
      // Filtro de categoria específica
      if (activeFilter !== 'all' && c.categoria !== activeFilter) return false;
      // Busca textual
      const nome = (c.nomeFantasia || c.razaoSocial || '').toLowerCase();
      if (search && !nome.includes(search.toLowerCase())) return false;
      // Filtro de cidade
      if (cityFilter && !(c.cidade || '').toLowerCase().includes(cityFilter.toLowerCase())) return false;
      return true;
    });
  }, [allClinics, activeFilter, search, cityFilter]);

  const filteredPros = useMemo(() => {
    return allProfessionals.filter(p => {
      // Filtro de categoria
      if (activeFilter !== 'all' && activeFilter !== 'autonomo') {
        const tags = CATEGORY_TAGS[activeFilter] || [];
        if (!tags.some(tag => (p.specialty || '').toLowerCase().includes(tag))) return false;
      }
      // Busca textual
      const nome = (p.name || '').toLowerCase();
      if (search && !nome.includes(search.toLowerCase()) &&
          !(p.specialty || '').toLowerCase().includes(search.toLowerCase())) return false;
      // Filtro de cidade
      if (cityFilter && !(p.endereco || '').toLowerCase().includes(cityFilter.toLowerCase())) return false;
      return true;
    });
  }, [allProfessionals, activeFilter, search, cityFilter]);

  const totalResults = filteredClinics.length + filteredPros.length;

  return (
    <div style={{ fontFamily: T.sans }}>
      <style>{`@keyframes shimmer { to { background-position: -200% 0; } }`}</style>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: T.serif, fontSize: 28, color: T.text }}>Hub de Serviços</div>
        <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
          Encontre clínicas, salões e profissionais para agendar.
        </div>
      </div>

      {/* ── Busca e cidade ───────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 2, minWidth: 200 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            color: T.muted, fontSize: 14 }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nome ou serviço..."
            style={{ width: '100%', padding: '10px 12px 10px 36px', border: `1.5px solid ${T.border}`,
              borderRadius: 10, fontSize: 13, fontFamily: T.sans, color: T.text, outline: 'none',
              boxSizing: 'border-box', background: '#fff' }} />
        </div>
        <div style={{ position: 'relative', flex: 1, minWidth: 160 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            color: T.muted, fontSize: 14 }}>📍</span>
          <input value={cityFilter} onChange={e => setCityFilter(e.target.value)}
            placeholder="Cidade ou bairro..."
            style={{ width: '100%', padding: '10px 12px 10px 36px', border: `1.5px solid ${T.border}`,
              borderRadius: 10, fontSize: 13, fontFamily: T.sans, color: T.text, outline: 'none',
              boxSizing: 'border-box', background: '#fff' }} />
        </div>
      </div>

      {/* ── Chips de categoria (filtros) ─────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {FILTER_CATEGORIES.map(cat => {
          const active = activeFilter === cat.id;
          return (
            <button key={cat.id}
              onClick={() => setActiveFilter(cat.id)}
              style={{
                padding: '7px 16px', border: `1.5px solid ${active ? T.brand : T.border}`,
                borderRadius: 20, background: active ? T.brand : '#fff',
                color: active ? '#fff' : T.text2, fontSize: 13, fontWeight: active ? 600 : 400,
                cursor: 'pointer', fontFamily: T.sans,
                display: 'inline-flex', alignItems: 'center', gap: 6,
                transition: 'all 0.15s',
              }}>
              <span style={{ fontSize: 14 }}>{cat.icon}</span>
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* ── Resultados ───────────────────────────────────────────────────── */}
      {loading ? (
        <>
          <div style={{ fontSize: 13, color: T.muted, marginBottom: 12 }}>Carregando...</div>
          <div style={{ display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 14 }}>
            {[1,2,3,4].map(i => <CardSkeleton key={i} />)}
          </div>
        </>
      ) : totalResults === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: T.muted }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
          <div style={{ fontFamily: T.serif, fontSize: 20, color: T.text, marginBottom: 8 }}>
            Nenhum resultado encontrado
          </div>
          <div style={{ fontSize: 13, maxWidth: 320, margin: '0 auto' }}>
            {search || cityFilter
              ? 'Tente ajustar os filtros ou buscar por outro termo.'
              : activeFilter !== 'all'
              ? 'Ainda não há estabelecimentos nesta categoria. Tente "Todos".'
              : 'Ainda não há estabelecimentos ou profissionais cadastrados.'}
          </div>
          {(search || cityFilter || activeFilter !== 'all') && (
            <button onClick={() => { setSearch(''); setCityFilter(''); setActiveFilter('all'); }}
              style={{ marginTop: 16, background: 'none', border: `1px solid ${T.border}`,
                borderRadius: 8, padding: '8px 18px', cursor: 'pointer',
                fontSize: 13, color: T.brand, fontFamily: T.sans }}>
              Limpar filtros
            </button>
          )}
        </div>
      ) : (
        <>
          <div style={{ fontSize: 13, color: T.muted, marginBottom: 16 }}>
            {totalResults} resultado{totalResults !== 1 ? 's' : ''} encontrado{totalResults !== 1 ? 's' : ''}
          </div>

          {/* Estabelecimentos */}
          {filteredClinics.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              {activeFilter === 'all' && (
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: 1, color: T.muted, marginBottom: 12 }}>
                  Estabelecimentos ({filteredClinics.length})
                </div>
              )}
              {/* align-items:stretch (padrão) faz todos os cards terem mesma altura na linha */}
              <div style={{ display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: 14, alignItems: 'stretch' }}>
                {filteredClinics.map(clinic => (
                  <ClinicCard key={clinic.id} clinic={clinic}
                    onSelect={() => navigate(`/client/clinic/${clinic.id}`)} />
                ))}
              </div>
            </div>
          )}

          {/* Profissionais autônomos */}
          {filteredPros.length > 0 && (
            <div>
              {activeFilter === 'all' && (
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: 1, color: T.muted, marginBottom: 12 }}>
                  Profissionais Autônomos ({filteredPros.length})
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {filteredPros.map(pro => (
                  <ProfessionalCard key={pro.id} pro={pro}
                    onSelect={() => navigate('/client/new-appointment', {
                      state: { professionalId: pro.id, professionalName: pro.name },
                    })} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
